import { detectOverflow, Middleware } from "@floating-ui/core";
import { getOverflowAncestors } from "@floating-ui/react-dom";

type DetectOverflowOptions = Parameters<typeof detectOverflow>[1];
type Options = DetectOverflowOptions & { showOverTrigger?: boolean };

/**
 * This is a middleware that detects if the popup overflows the top or
 * left edge of the scrollable parent. If it does it will reset placement to
 * another one so the user will be able scroll the parent and see the full content
 * of the popup.
 *
 * Should be placed after native float-ui middleware with 'bestFit' fallbackStrategy
 */
export const flipBestFitEnhancer = ({ showOverTrigger, ...options }: Options = {}): Middleware => ({
    name: "flipBestFitEnhancer",
    options,
    async fn(middlewareArguments) {
        const {
            elements,
            placement,
            middlewareData: { flip },
        } = middlewareArguments;

        let overflow = await detectOverflow(middlewareArguments, options);

        // round overflows because with browser zoom zoom (110%,90%) floating-ui
        // calculates that there is small overflow (overflow.left like 0.0911) even if there is
        // no overflow.
        overflow = { ...overflow, top: Math.floor(overflow.top), left: Math.floor(overflow.left) };

        if (overflow.left > 0 || overflow.top > 0) {
            const { scrollTop, scrollLeft } = getOverflowParentScrolls(elements.floating);

            const canScrollToSeeOverflowContent = overflow.left <= scrollLeft && overflow.top <= scrollTop;
            if (!canScrollToSeeOverflowContent && flip.overflows) {
                // ask flip middleware to select another bestFit placement.
                flip.overflows = flip.overflows.filter(x => x.placement !== placement);
                if (flip.overflows.length) {
                    return {
                        reset: { placement: flip.overflows[0].placement },
                    };
                }
            }
        }

        return {};
    },
});

const getOverflowParentScrolls = (element: HTMLElement) => {
    const parent = getOverflowAncestors(element)[0] as HTMLElement & typeof window;
    if (parent === window) {
        return { scrollTop: window.scrollY, scrollLeft: window.scrollX };
    }

    const scrollTop = parent?.scrollTop ?? 0;
    const scrollLeft = parent?.scrollLeft ?? 0;

    return { scrollTop, scrollLeft };
};
